// src/api/user-service/bookmarkService.js
import client from '../client';

const TARGET_TYPE_MAP = {
  FARM: 'farm',
  EVENT: 'event',
  STAY: 'stay',
};

function normalizeTargetType(targetType) {
  if (!targetType) return targetType;
  if (typeof targetType !== 'string') return targetType;

  // allow passing 'FARM' | 'EVENT' | 'STAY' or already normalized 'farm' | 'event' | 'stay'
  return TARGET_TYPE_MAP[targetType] || targetType;
}

/**
 * Create a bookmark
 * @param {Object} data - { targetType: 'FARM' | 'EVENT' | 'STAY' | 'farm' | 'event' | 'stay', targetId: number, note?: string }
 * @returns {Promise<Object>} Created bookmark data
 */
export const createBookmark = async (data) => {
  const payload = {
    ...data,
    targetType: normalizeTargetType(data?.targetType),
  };
  return client.post('/users/me/bookmarks', payload).then(res => res.data);
};

/**
 * Get my bookmarks list
 * @param {Object} query - { page?: number, limit?: number, targetType?: string, sort?: string, order?: 'ASC' | 'DESC' }
 * @returns {Promise<Object>} Paginated bookmarks list
 */
export const getMyBookmarks = async (query = {}) => {
  const params = {
    ...query,
    targetType: normalizeTargetType(query?.targetType),
  };
  return client.get('/users/me/bookmarks', { params }).then(res => res.data);
};

/**
 * Check if item is bookmarked
 * @param {string} targetType - 'FARM' | 'EVENT' | 'STAY' | 'farm' | 'event' | 'stay'
 * @param {number} targetId - Target item ID
 * @returns {Promise<Object>} { bookmarked: boolean, bookmarkId?: number }
 */
export const isBookmarked = async (targetType, targetId) => {
  return client.get('/users/me/bookmarks/check', {
    params: { targetType: normalizeTargetType(targetType), targetId },
  }).then(res => res.data);
};

/**
 * Update bookmark (e.g., update note)
 * @param {number} bookmarkId - Bookmark ID
 * @param {Object} data - { note?: string }
 * @returns {Promise<Object>} Updated bookmark data
 */
export const updateBookmark = async (bookmarkId, data) => {
  return client.patch(`/users/me/bookmarks/${bookmarkId}`, data).then(res => res.data);
};

/**
 * Delete bookmark
 * @param {number} bookmarkId - Bookmark ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteBookmark = async (bookmarkId) => {
  return client.delete(`/users/me/bookmarks/${bookmarkId}`).then(res => res.data);
};

/**
 * Delete bookmark by target (alternative method)
 * @param {string} targetType - 'FARM' | 'EVENT' | 'STAY' | 'farm' | 'event' | 'stay'
 * @param {number} targetId - Target item ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteBookmarkByTarget = async (targetType, targetId) => {
  return client.delete(`/users/me/bookmarks/${normalizeTargetType(targetType)}/${targetId}`).then(res => res.data);
};
